/**
 * Client-side plain-language preview for the automation-rule builder (SE-5506).
 *
 * There is no canonical preview field on the schema, so the builder generates
 * the "what this rule will do" sentence itself — a PURE function of the
 * in-progress form plus name lookups for the stages/tags referenced by id. It
 * is called on every keystroke/selection so the manager sees the rule in
 * English before saving (AC: "a live plain-language preview updates as they
 * build it, before saving").
 *
 * Kept free of React and of the generated document types so the preview
 * invariants can be asserted directly, table-driven, in a unit test.
 * @module features/player-kanban/utils/automationRulePreview
 */

import {
  PipelineAutomationActionType,
  PipelineAutomationComparator,
  PipelineAutomationTriggerType,
} from "@/generated/graphql";

import type {
  AutomationRuleConditionForm,
  AutomationRuleFormState,
} from "../types/automationRules";
import {
  CONDITION_VALUE_KIND,
  conditionTypeLabel,
} from "./automationRuleOptions";
import { outcomeLabel } from "./outcomeLabels";

/** Name resolvers so the preview can spell out stages/tags referenced by id. */
export interface AutomationRulePreviewLookups {
  /** Resolve a phase id to its display name. */
  readonly phaseName: (id: string | null | undefined) => string;
  /** Resolve a tag id to its display name. */
  readonly tagName: (id: string | null | undefined) => string;
}

/** Placeholder used when a referenced stage/tag/value has not been chosen yet. */
const UNSET = "…";

/** Word form for each comparator, read left-to-right in a sentence. */
const COMPARATOR_WORDS: Record<PipelineAutomationComparator, string> = {
  [PipelineAutomationComparator.Eq]: "is",
  [PipelineAutomationComparator.Gt]: "is greater than",
  [PipelineAutomationComparator.Gte]: "is at least",
  [PipelineAutomationComparator.Lt]: "is less than",
  [PipelineAutomationComparator.Lte]: "is at most",
};

/**
 * Render one condition as a clause (e.g. "GPR is at least 70").
 * @param condition - The condition form row.
 * @param lookups - Name resolvers.
 * @returns The clause text.
 */
const conditionClause = (
  condition: AutomationRuleConditionForm,
  lookups: AutomationRulePreviewLookups
): string => {
  const kind = CONDITION_VALUE_KIND[condition.conditionType];

  if (kind === "stage") {
    return `the card is in ${lookups.phaseName(condition.kanbanPhaseId)}`;
  }

  if (kind === "text") {
    const value = condition.textValue.trim() || UNSET;
    return `${conditionTypeLabel(condition.conditionType)} is "${value}"`;
  }

  // numeric
  const comparator = condition.comparator
    ? COMPARATOR_WORDS[condition.comparator]
    : "is";
  const value = condition.numericValue.trim() || UNSET;
  return `${conditionTypeLabel(condition.conditionType)} ${comparator} ${value}`;
};

/**
 * Join every condition into an "and"-combined clause, or empty string if none.
 * @param conditions - The condition form rows.
 * @param lookups - Name resolvers.
 * @returns The combined clause, or "".
 */
const conditionsClause = (
  conditions: readonly AutomationRuleConditionForm[],
  lookups: AutomationRulePreviewLookups
): string =>
  conditions
    .map(condition => conditionClause(condition, lookups))
    .join(" and ");

/**
 * One clause-builder: given the form + lookups, produce a sentence fragment.
 * Kept as a value type so the phrase tables below can be exhaustive `Record`s.
 */
type PhraseBuilder = (
  form: AutomationRuleFormState,
  lookups: AutomationRulePreviewLookups
) => string;

/**
 * Trigger → phrase builder. Exhaustive over the trigger enum: a value added to
 * the schema (and regenerated) is a COMPILE error here rather than silently
 * falling through to a generic default.
 */
const TRIGGER_PHRASES: Record<PipelineAutomationTriggerType, PhraseBuilder> = {
  [PipelineAutomationTriggerType.CardAddedToPipeline]: () =>
    "a card is added to the pipeline",
  [PipelineAutomationTriggerType.CardEnteredStage]: (form, lookups) =>
    `a card enters ${lookups.phaseName(form.triggerKanbanPhaseId)}`,
  [PipelineAutomationTriggerType.ConditionBecameTrue]: () =>
    "a condition becomes true",
  [PipelineAutomationTriggerType.TimeInStageExceeded]: (form, lookups) =>
    `a card's time in ${lookups.phaseName(
      form.triggerKanbanPhaseId
    )} exceeds the limit`,
  [PipelineAutomationTriggerType.PeriodicWeekly]: () => "each week",
};

/**
 * Action → phrase builder. Exhaustive over the action enum (see
 * {@link TRIGGER_PHRASES}).
 */
const ACTION_PHRASES: Record<PipelineAutomationActionType, PhraseBuilder> = {
  [PipelineAutomationActionType.AddToReviewQueue]: () =>
    "add it to the review queue",
  [PipelineAutomationActionType.MoveToStage]: (form, lookups) => {
    const target = lookups.phaseName(form.targetKanbanPhaseId);
    const outcome = form.requestedOutcome
      ? ` (${outcomeLabel(form.requestedOutcome)})`
      : "";
    return `move it to ${target}${outcome}`;
  },
  [PipelineAutomationActionType.ApplyTag]: (form, lookups) =>
    `apply the "${lookups.tagName(form.kanbanTagId)}" tag`,
  [PipelineAutomationActionType.RemoveTag]: (form, lookups) =>
    `remove the "${lookups.tagName(form.kanbanTagId)}" tag`,
  [PipelineAutomationActionType.NotifyPipelineOwner]: () =>
    "notify the pipeline owner",
  // SE-5628. The builder does not offer this action (the rule type does not
  // return `assignedToUserId`, so an edit could not round-trip), but a rule
  // carrying it can still arrive from the API and the panel must describe it.
  // No member name is available for the same reason, so the phrase stays
  // deliberately unspecific rather than inventing one.
  [PipelineAutomationActionType.Assign]: () => "assign it to a member",
};

/**
 * Render the trigger half of an Automation sentence.
 * @param form - The builder form state.
 * @param lookups - Name resolvers.
 * @returns The trigger phrase (no leading capital).
 */
const triggerPhrase = (
  form: AutomationRuleFormState,
  lookups: AutomationRulePreviewLookups
): string => TRIGGER_PHRASES[form.triggerType](form, lookups);

/**
 * Render the action half of an Automation sentence.
 * @param form - The builder form state.
 * @param lookups - Name resolvers.
 * @returns The action phrase (no leading capital).
 */
const actionPhrase = (
  form: AutomationRuleFormState,
  lookups: AutomationRulePreviewLookups
): string => ACTION_PHRASES[form.actionType](form, lookups);

/**
 * Build the guard sentence: a condition-gated stage entry that routes the card
 * to the review queue.
 * @param form - The builder form state.
 * @param lookups - Name resolvers.
 * @returns The plain-language sentence.
 */
const guardPreview = (
  form: AutomationRuleFormState,
  lookups: AutomationRulePreviewLookups
): string => {
  const stage = lookups.phaseName(form.triggerKanbanPhaseId);
  const clause = conditionsClause(form.conditions, lookups);
  const when = clause
    ? `When a card enters ${stage} and ${clause}`
    : `When a card enters ${stage}`;
  return `${when}, add it to the review queue.`;
};

/**
 * Build the automation sentence: a trigger, optional conditions, and an action.
 * @param form - The builder form state.
 * @param lookups - Name resolvers.
 * @returns The plain-language sentence.
 */
const automationPreview = (
  form: AutomationRuleFormState,
  lookups: AutomationRulePreviewLookups
): string => {
  const trigger = triggerPhrase(form, lookups);
  const clause = conditionsClause(form.conditions, lookups);
  const when = clause ? `When ${trigger} and ${clause}` : `When ${trigger}`;
  return `${when}, ${actionPhrase(form, lookups)}.`;
};

/**
 * Produce the live plain-language preview for the current builder state.
 *
 * Deterministic and side-effect-free: same form + same lookups → same string,
 * so it is safe to call on every render.
 * @param form - The in-progress builder form state.
 * @param lookups - Name resolvers for stages/tags referenced by id.
 * @returns The preview sentence.
 */
export const buildAutomationRulePreview = (
  form: AutomationRuleFormState,
  lookups: AutomationRulePreviewLookups
): string =>
  form.kind === "guard"
    ? guardPreview(form, lookups)
    : automationPreview(form, lookups);
